/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {RulesetApiService, RulesetModel} from './ruleset-api.service';
import {RulesetEditPageComponent} from './ruleset-edit-page.component';
import {RulesetRoutePageComponent} from './ruleset-route-page.component';

export function rulesetStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(RulesetApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  // Matches RulesetEditPageComponent.canUpdateSiteRuleset plus the AX_PUBLISHER list access.
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisher.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.axPublisherAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisherTeamAdmin.apiKey);
  const rh = adnRouterHelper.createRouterHelper('rulesets.ruleset', RulesetApiService, 'app.inv', 'app');

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: RulesetRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {
        component: ListDefaultActionsComponent,
        bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}
      },
      'title@app': stateTitleView()
    }
  }));

  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      rulesetPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          const model: RulesetModel = {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []};
          return new SignalAdminResourcePageState(model, true, readOnly(), api, 'Ruleset');
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'Ruleset'));
      },
      model: function(rulesetPage: SignalAdminResourcePageState<RulesetModel>) {
        return rulesetPage.model();
      }
    },
    views: {
      'content@app': {component: RulesetEditPageComponent, bindings: {state: 'rulesetPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'rulesetPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'rulesetPage'}}
    }
  }));

  return states.build();
}
